import { App, Button, Popconfirm, Tag } from 'antd';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import type { GroupedSessionDto } from '../../../interfaces/SessionApiService';
import { SessionService } from '../../../services/sessionApiService';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import { Icon } from '../../Core/Icon';

// Create a session service instance to interact with session endpoints
const sessionService = new SessionService();

interface UserSessionsFormProps {
  onLogout?: () => void;
  /** Admin mode: user ID to manage sessions for */
  userId?: number;
  /** Admin mode: user name to display */
  userName?: string;
  /** Callback when modal should be closed */
  onClose?: () => void;
}

export default function UserSessionsForm({
  onLogout,
  userId,
  userName,
  onClose,
}: UserSessionsFormProps) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const formatTimestamp = useFormatTimestamp();

  // Store sessions for the current or administered user
  const [sessions, setSessions] = useState<GroupedSessionDto[]>([]);
  // Indicates whether sessions are being fetched
  const [isLoading, setIsLoading] = useState(false);
  // Stores the IDs of sessions currently being revoked
  const [revokingSessionIds, setRevokingSessionIds] = useState<number[] | null>(null);
  // Indicates whether "revoke other sessions" is in progress
  const [isRevokingOthers, setIsRevokingOthers] = useState(false);
  // Indicates whether "revoke all sessions" is in progress
  const [isRevokingAll, setIsRevokingAll] = useState(false);

  // Determine if the form is in admin mode (i.e., userId is specified)
  const isAdminMode = userId !== undefined;

  /**
   * Fetch sessions for the user, or for the administered user if in admin mode.
   * Handles API errors and loading indication.
   */
  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch sessions depending on the mode
      const data = isAdminMode
        ? await sessionService.adminGetUserSessionsGrouped(userId)
        : await sessionService.getActiveSessionsGrouped();
      setSessions(data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      notification.error({ message: t('errors.sessionsFetchError') });
    } finally {
      setIsLoading(false);
    }
  }, [notification, t, isAdminMode, userId]);

  // On mount and when fetchSessions changes, fetch sessions
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  /**
   * Handles revocation of a set of sessions (by their IDs).
   * Uses different endpoints in admin mode. Shows loading and handles errors.
   */
  const handleRevokeSession = async (sessionIds: number[]) => {
    // Check if we're revoking the current session
    const isRevokingCurrent = sessions.some(
      (s) => s.isCurrent && s.ids.some((id) => sessionIds.includes(id)),
    );

    setRevokingSessionIds(sessionIds);
    try {
      if (isAdminMode) {
        // Admin: one DELETE /sessions/admin/user/{userId} instead of N × DELETE .../admin/{sessionId}
        if (userId === undefined) {
          notification.error({ message: t('errors.sessionsRevokeError') });
          return;
        }
        const result = await sessionService.adminRevokeUserSessions(userId);
        message.success(t('modal.userSessionsForm.sessionRevoked', { count: result.revokedCount }));
        fetchSessions();
      } else {
        const result = await sessionService.revokeMultipleSessions(sessionIds);
        message.success(t('modal.userSessionsForm.sessionRevoked', { count: result.revokedCount }));
        // If current session was revoked, close modal first then trigger logout
        if (isRevokingCurrent) {
          onClose?.();
          onLogout?.();
        } else {
          fetchSessions();
        }
      }
    } catch (error: any) {
      if (error.status === 403) {
        notification.error({ message: t('errors.sessionsRevokeForbiddenError') });
      } else {
        notification.error({ message: t('errors.sessionsRevokeError') });
      }
    } finally {
      setRevokingSessionIds(null);
    }
  };

  /**
   * Revokes all sessions except the current one.
   */
  const handleRevokeOthers = async () => {
    setIsRevokingOthers(true);
    try {
      const result = await sessionService.revokeOtherSessions();
      message.success(
        t('modal.userSessionsForm.otherSessionsRevoked', { count: result.revokedCount }),
      );
      fetchSessions();
    } catch (error: any) {
      if (error.status === 403) {
        notification.error({ message: t('errors.sessionsRevokeForbiddenError') });
      } else {
        notification.error({ message: t('errors.sessionsRevokeOthersError') });
      }
    } finally {
      setIsRevokingOthers(false);
    }
  };

  /**
   * Handles revocation of all sessions (for current or administered user).
   * Calls relevant endpoints and fires onLogout if in user mode.
   */
  const handleRevokeAll = async () => {
    setIsRevokingAll(true);
    try {
      if (isAdminMode) {
        const result = await sessionService.adminRevokeUserSessions(userId);
        message.success(
          t('modal.userSessionsForm.allSessionsRevoked', { count: result.revokedCount }),
        );
        fetchSessions();
      } else {
        await sessionService.revokeAllSessions();
        message.success(t('modal.userSessionsForm.allSessionsRevoked'));
        onClose?.();
        onLogout?.();
      }
    } catch (error: any) {
      if (error.status === 403) {
        notification.error({ message: t('errors.sessionsRevokeForbiddenError') });
      } else {
        notification.error({ message: t('errors.sessionsRevokeAllError') });
      }
    } finally {
      setIsRevokingAll(false);
    }
  };

  /**
   * Chooses an icon type for a session based on device information.
   * Returns a "mobile" or "desktop" icon name.
   */
  const getDeviceIcon = (
    session: GroupedSessionDto,
  ): 'HardDriveIcon' | 'DeviceAccessLimitationIcon' => {
    const deviceName = session.deviceName?.toLowerCase() || '';
    if (
      deviceName.includes('mobile') ||
      deviceName.includes('phone') ||
      deviceName.includes('tablet') ||
      deviceName.includes('ipad') ||
      deviceName.includes('android') ||
      deviceName.includes('iphone') ||
      deviceName.includes('ios')
    ) {
      return 'DeviceAccessLimitationIcon';
    }
    return 'HardDriveIcon';
  };

  // Count the number of sessions that are NOT the current session
  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div className="w-full">
      {/* Loading spinner while fetching sessions */}
      {isLoading ? (
        <div className="flex w-full items-center justify-center py-8">
          <Icon name="LoaderIcon" className="text-blue-dark h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="max-h-[60vh] w-full overflow-y-auto">
          <ListyWithEmpty
            className="w-full"
            items={sessions}
            emptyText={t('modal.userSessionsForm.noSessions')}
            rowKey={(session) => session.ids.join(',')}
            itemRender={(session: GroupedSessionDto) => (
              <ListyMetaRow
                className={clsx('rounded-md px-2', {
                  'bg-blue-light/10': session.isCurrent,
                })}
                avatar={
                  <div
                    className={clsx(
                      'flex h-10 w-10 items-center justify-center rounded-full',
                      session.isCurrent ? 'bg-blue-dark/10' : 'bg-gray-light',
                    )}
                  >
                    <Icon
                      name={getDeviceIcon(session)}
                      className={clsx(
                        'h-5 w-5',
                        session.isCurrent ? 'text-blue-dark' : 'text-gray-dark',
                      )}
                    />
                  </div>
                }
                title={
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-blue-dark text-sm font-medium">
                      {session.browserName || t('modal.userSessionsForm.unknownBrowser')}
                    </span>
                    {session.ipAddress && (
                      <span className="text-gray-dark text-xs font-normal">
                        ({session.ipAddress})
                      </span>
                    )}
                    {session.isCurrent && (
                      <Tag color="blue" className="rounded-full!">
                        {t('modal.userSessionsForm.currentSession')}
                      </Tag>
                    )}
                  </div>
                }
                description={
                  <div className="text-gray-dark space-y-1 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon name="HardDriveIcon" className="h-3 w-3" />
                      <span>{session.deviceName || t('modal.userSessionsForm.unknownDevice')}</span>
                    </div>
                    {session.lastCreatedAt && (
                      <div className="flex items-center gap-1">
                        <Icon name="HistoryIcon" className="h-3 w-3" />
                        <span>
                          {t('modal.userSessionsForm.lastActivity')}:{' '}
                          {formatTimestamp(new Date(session.lastCreatedAt))}
                        </span>
                      </div>
                    )}
                    {session.expiresAt && (
                      <div className="flex items-center gap-1">
                        <Icon name="CalendarClockIcon" className="h-3 w-3" />
                        <span>
                          {t('modal.userSessionsForm.expiresAt')}:{' '}
                          {new Date(session.expiresAt).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                }
                actions={
                  <Popconfirm
                    title={
                      isAdminMode
                        ? t('modal.userSessionsForm.adminRevokeAllConfirmTitle')
                        : t('modal.userSessionsForm.revokeSessionConfirmTitle')
                    }
                    description={
                      isAdminMode
                        ? t('modal.userSessionsForm.adminRevokeAllConfirmDescription', {
                            userName: userName || '',
                          })
                        : session.sessionCount > 1
                          ? t('modal.userSessionsForm.revokeGroupedSessionConfirmDescription', {
                              count: session.sessionCount,
                            })
                          : t('modal.userSessionsForm.revokeSessionConfirmDescription')
                    }
                    onConfirm={() => handleRevokeSession(session.ids)}
                    okText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      size="small"
                      danger
                      loading={
                        revokingSessionIds !== null &&
                        session.ids.some((id) => revokingSessionIds.includes(id))
                      }
                      icon={<Icon name="XIcon" className="h-4 w-4" />}
                    >
                      {t('modal.userSessionsForm.revokeSession')}
                    </Button>
                  </Popconfirm>
                }
              />
            )}
          />
        </div>
      )}

      {/* Sticky footer with session management actions */}
      {(onClose || sessions.length > 0 || (!isAdminMode && otherSessionsCount > 0)) && (
        <div className="border-gray-medium mt-4 flex justify-between gap-2 border-t pt-4">
          <div className="flex gap-2">
            {/* Show "Logout other sessions" for non-admin when there are multiple sessions */}
            {!isAdminMode && otherSessionsCount > 0 && (
              <Popconfirm
                title={t('modal.userSessionsForm.revokeOthersConfirmTitle')}
                description={t('modal.userSessionsForm.revokeOthersConfirmDescription')}
                onConfirm={handleRevokeOthers}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
              >
                <Button danger loading={isRevokingOthers} disabled={isRevokingAll}>
                  {t('modal.userSessionsForm.logoutOthers')}
                </Button>
              </Popconfirm>
            )}
            {/* Show "Logout all" or "Revoke all user sessions" depending on mode */}
            {sessions.length > 0 && (
              <Popconfirm
                title={
                  isAdminMode
                    ? t('modal.userSessionsForm.adminRevokeAllConfirmTitle')
                    : t('modal.userSessionsForm.revokeAllConfirmTitle')
                }
                description={
                  isAdminMode
                    ? t('modal.userSessionsForm.adminRevokeAllConfirmDescription', {
                        userName: userName || '',
                      })
                    : t('modal.userSessionsForm.revokeAllConfirmDescription')
                }
                onConfirm={handleRevokeAll}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
              >
                <Button type="text" danger loading={isRevokingAll} disabled={isRevokingOthers}>
                  {isAdminMode
                    ? t('modal.userSessionsForm.revokeAllUserSessions')
                    : t('modal.userSessionsForm.logoutAll')}
                </Button>
              </Popconfirm>
            )}
          </div>
          {onClose ? <Button onClick={onClose}>{t('common.close')}</Button> : null}
        </div>
      )}
    </div>
  );
}
